import { Card, Box, Typography, IconButton, Tooltip, Badge, Stack } from "@mui/material"
import { ListContextProvider, ReduxState } from "ra-core"
import { FC, useMemo } from "react"
import { FunctionField } from "@/components/field"
import { Datagrid } from "@/components/list/datagrid"
import useLocalListController from "@/components/list/useLocalListController"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { EncryptionMappingResponseDto } from "@/gen/api/governance"
import { Link as RouterLink } from 'react-router-dom'
import AddLinkIcon from '@mui/icons-material/AddLink';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DateField2 from "@/components/field/DateField2"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import MappedIcon from "@/icons/Mapped";
import SkippedIcon from "@/icons/Skipped";
import PendingReviewIcon from "@/icons/PendingReview";
import { genUrlWithHashid } from "@/utils/hashid"
import { useSelector } from "react-redux"
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "@/components/list/datagridFunctionWidgets"
import DatabaseNameField from "@/components/databases/DatabaseNameField"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_ONDATA_CLIENTS } from "@/constants"
import { nonSqlDbTypes } from "@/components/obfuscation/defs"


const resource = 'encryption/mappings'


const databaseListOptions : {sorters: Record<string, (a:EncryptionMappingResponseDto & RecPatch, b:EncryptionMappingResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    databaseName: (a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1,
    mappingCreatedTime: (a, b) => (a.mappingCreatedTime || '') < (b.mappingCreatedTime || '') ? -1 : 1,
    mappingModifiedTime: (a, b) => (a.mappingModifiedTime || '') < (b.mappingModifiedTime || '') ? -1 : 1,
  }
}

export const DatabaseMappingList: FC = () => {
  const hasClassifyPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION]),
    hasClientPermit = useHasPermit([MANAGE_ONDATA_CLIENTS])
  const databasesCtrl = useLocalListController<EncryptionMappingResponseDto & RecPatch>({ resource, basePath: '' }, databaseListOptions)

  const data = useSelector<ReduxState, Record<number, EncryptionMappingResponseDto>>(store => store.admin.resources[resource as string].data as any)
  const dbList = Object.values(data || {});
  
  const engineOptions = useMemo(() => Object.values(
    dbList.reduce((acc, d) => {
      const label = `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`
      const option = ({
        label,
        value: { engineVersion: d.engineVersion, engineEdition: d.engineEdition }
      })
      acc[label] = option

      return acc
    }, {} as Record<string, {label:string,value:{engineVersion:any, engineEdition:any}}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(dbList.map(d => `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`))].sort()) ])
  

  const persistFilter = useMemo(() => ({
    existsFields: (x: EncryptionMappingResponseDto) => {
      return [x.mappedCount, x.skippedCount, x.pendingReviewCount]
        .reduce((acc: number, c) => { return acc + (Number.isInteger(c) ? (c as number) : 0) }, 0) > 0
    },
  }), [])

  return (
    <ListContextProvider value={databasesCtrl}>
      <EncryptedDatabasesContextProvider>
        <Card>
          <AtopFuncArea persistFilters={persistFilter}>
            <SearchField 
              sourceKey="databaseName"
              match={(d: EncryptionMappingResponseDto, fval) => !!d.databaseName && d.databaseName.toLowerCase().includes((fval as string).trim().toLowerCase())}
              sx={{ width: 280 }}
              TextFieldProps={{ placeholder: 'Search by Database Name' }}
            />
            <FilterField 
              label="Engine" 
              filterOptions={engineOptions} 
              sourceKey="engine"
              match={(d: EncryptionMappingResponseDto, fval:FilterOption|'_empty_') => {
                if (fval === '_empty_') return true
                const value = fval.value
                return d.engineVersion === value.engineVersion && d.engineEdition === value.engineEdition
              }}
              sx={{ width: 320 }}
            />
          </AtopFuncArea>
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150 }}>
              <Datagrid
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                  '& tr div.MuiBox-root': {
                    '& div.encryptedDbTip': { top: theme => theme.spacing(-3.25) },
                    '& span.encryptedDbTipText': { top: theme => theme.spacing(-3.25) },
                  },
                }}
                {...databasesCtrl}
              >
                <DatabaseNameField label="Database" keyMap={{ name: 'databaseName' }} />
                <FunctionField 
                  source="engineVersion" 
                  label="Engine" 
                  sortable={false} 
                  render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                    <Typography
                      component="p"
                      variant="body2"
                    >
                      {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                    </Typography>
                  )}
                />
                <DateField2 source="mappingCreatedTime" label="Creation Time" showTime />
                <DateField2 source="mappingModifiedTime" label="Last Modified Time" showTime />
                <FunctionField 
                  source="fieldsMapped" 
                  label="Fields" 
                  sortable={false} 
                  render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                    <Stack direction="row" spacing={1} >
                      <Tooltip title="Mapped">
                        <IconButton color={"inherit" as any}>
                          <Badge color={"success" as any} badgeContent={d?.mappedCount || 0} showZero max={9999}>
                            <MappedIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Skipped">
                        <IconButton color={"inherit"}>
                          <Badge color="secondary" badgeContent={d?.skippedCount || 0} showZero max={9999}>
                            <SkippedIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Pending Review">
                        <IconButton color={"inherit" as any}>
                          <Badge color={"warning" as any} badgeContent={d?.pendingReviewCount || 0} showZero max={9999} 
                            sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                          >
                            <PendingReviewIcon />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                />
                <FunctionField 
                  label="Action"
                  sortable={false}
                  render={(d?: EncryptionMappingResponseDto & RecPatch) => {
                    return (
                      <Stack direction="row"
                        sx={{
                          '& .MuiIconButton-root': {
                            color: 'text.primary' as any, 
                            '&:hover': { backgroundColor: 'action.hover' },
                          }
                        }}
                      >
                      {hasClientPermit &&
                        <Tooltip title="Assign Clients">
                          <IconButton
                            component={RouterLink}
                            to={genUrlWithHashid(`/encryption/mappings/$0/clients`,
                              d?.databaseId || 0, d?.databaseName
                            )}
                          >
                            <AddLinkIcon />
                          </IconButton>
                        </Tooltip>}
                      {((d?.mappedCount || 0) + (d?.skippedCount || 0) + (d?.pendingReviewCount || 0)) > 0 &&
                        <Tooltip title={hasClassifyPermit ? "Edit" : "View"}>
                          <IconButton 
                            component={RouterLink}
                            to={genUrlWithHashid(
                              nonSqlDbTypes.has(d!.databaseType!) 
                                ? `/encryption/mappings/$0/ns/show` 
                                : `/encryption/mappings/$0/show`, 
                              d?.databaseId || 0, d?.databaseName
                            )}
                          >
                            {hasClassifyPermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>}
                      </Stack>
                    )
                  }}
                />
              </Datagrid>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...databasesCtrl} />}
        </Card>
      </EncryptedDatabasesContextProvider>
    </ListContextProvider>
  )
}


export default DatabaseMappingList;

